import { AnimatePresence, motion } from 'framer-motion';
import { Languages, Menu, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink } from 'react-router-dom';
import { brand } from '../../data/siteData.js';

const links = [
  ['/', 'nav.home'],
  ['/about', 'nav.about'],
  ['/services', 'nav.services'],
  ['/projects', 'nav.projects'],
  ['/contact', 'nav.contact'],
];

const navClass = ({ isActive }) =>
  `rounded-lg px-3 py-2 text-sm font-bold transition ${
    isActive ? 'bg-beam-blue text-white' : 'text-beam-ink/80 hover:bg-beam-blue/10 hover:text-beam-blue'
  }`;

export const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);

  const changeLanguage = (language) => {
    i18n.changeLanguage(language);
  };

  const LanguageSwitch = () => (
    <div className="flex items-center gap-1 rounded-lg border border-beam-blue/10 bg-white p-1 shadow-sm">
      <Languages className="ml-2 h-4 w-4 text-beam-blue" aria-hidden="true" />
      {['fr', 'en'].map((language) => (
        <button
          key={language}
          type="button"
          onClick={() => changeLanguage(language)}
          className={`focus-ring rounded-md px-2.5 py-1.5 text-xs font-extrabold uppercase transition ${
            i18n.language === language ? 'bg-beam-ink text-white' : 'text-beam-graphite hover:bg-beam-light'
          }`}
        >
          {language}
        </button>
      ))}
    </div>
  );

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/50 bg-white/80 backdrop-blur-xl">
      <nav className="container-beam flex h-[var(--nav-height)] items-center justify-between">
        <Link to="/" className="focus-ring rounded-lg" aria-label="BEAM home">
          <img src={brand.logo} alt="BEAM" className="h-8 w-auto sm:h-9" />
        </Link>

        <div className="hidden items-center gap-1 lg:flex">
          {links.map(([to, label]) => (
            <NavLink key={to} to={to} className={navClass}>
              {t(label)}
            </NavLink>
          ))}
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          <LanguageSwitch />
        </div>

        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="focus-ring inline-flex h-11 w-11 items-center justify-center rounded-lg border border-beam-blue/10 bg-white text-beam-ink lg:hidden"
          aria-label={t('nav.menu')}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      <AnimatePresence>
        {open ? (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="border-t border-beam-blue/10 bg-white lg:hidden"
          >
            <div className="container-beam grid gap-2 py-4">
              {links.map(([to, label]) => (
                <NavLink key={to} to={to} className={navClass} onClick={() => setOpen(false)}>
                  {t(label)}
                </NavLink>
              ))}
              <div className="mt-2 flex items-center justify-between gap-3">
                <LanguageSwitch />
              </div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </header>
  );
};
